import { useState, useEffect, useCallback } from 'react';
import './App.css';
import Auth from './Auth';
import supabase from './supabase';

const FREE_LIMIT = 3;

function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usageCount, setUsageCount] = useState(0);
  const [isPro, setIsPro] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });
    supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
  }, []);

  const fetchUsage = useCallback(async (userId) => {
    const { data } = await supabase
      .from('users')
      .select('free_uses, is_pro')
      .eq('id', userId)
      .single();
    if (data) {
      setUsageCount(data.free_uses);
      setIsPro(data.is_pro || false);
    } else {
      await supabase.from('users').insert({ id: userId, email: user.email, free_uses: 0 });
      setUsageCount(0);
      setIsPro(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchUsage(user.id);
    }
  }, [user, fetchUsage]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setResult(null);
    setImage(null);
    setPreview(null);
  };

  if (authLoading) return <div className="app"><div className="main">Loading...</div></div>;
  if (!user) return <Auth onLogin={setUser} />;

  // Handle Stripe success redirect — wait 3 seconds then re-fetch to catch webhook
  if (window.location.pathname === '/success') {
    setTimeout(() => {
      fetchUsage(user.id).then(() => {
        window.history.replaceState({}, '', '/');
      });
    }, 3000);
    return (
      <div className="app">
        <header className="header"><h1>🌿 What Is This Plant?</h1></header>
        <main className="main" style={{ textAlign: 'center', paddingTop: 60 }}>
          <div style={{ fontSize: 48 }}>✅</div>
          <h2 style={{ marginTop: 16, color: '#1b4332' }}>Payment Successful!</h2>
          <p style={{ color: '#555', marginTop: 8 }}>Activating your Pro account...</p>
        </main>
      </div>
    );
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImage(file);
    setPreview(URL.createObjectURL(file));
    setResult(null);
    setError(null);
  };

  const extractCommonName = (text) => {
    if (!text) return null;
    const patterns = [
      /commonly known as (?:the )?([A-Za-z][a-z]+(?:\s[a-z]+)*)/,
      /known as (?:the )?([A-Za-z][a-z]+(?:\s[a-z]+)*)/,
      /common name[s]? (?:is |are )?(?:the )?([A-Za-z][a-z]+(?:\s[a-z]+)*)/,
    ];
    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match) return match[1].charAt(0).toUpperCase() + match[1].slice(1);
    }
    return null;
  };

  const fetchWikiDetails = async (scientificName) => {
    try {
      const res = await fetch(
        `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(scientificName)}`
      );
      if (!res.ok) return null;
      const data = await res.json();
      const titleDiffersFromScientific = data.title?.toLowerCase() !== scientificName.toLowerCase();
      const commonName = titleDiffersFromScientific ? data.title : extractCommonName(data.extract);
      return {
        commonName,
        description: data.extract || null,
        thumbnail: data.thumbnail?.source || null,
        wikiUrl: data.content_urls?.desktop?.page || null,
      };
    } catch {
      return null;
    }
  };

  const handleIdentify = async () => {
    if (!isPro && usageCount >= FREE_LIMIT) {
      setError('You have used all 3 free identifications. Scroll down to upgrade!');
      return;
    }

    if (!image) {
      setError('Please select an image first.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const base64Full = await toBase64(image);
      const base64 = base64Full.split(',')[1];
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/identify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: base64 }),
      });

      if (!response.ok) {
        setError('API error ' + response.status);
        setLoading(false);
        return;
      }

      const data = await response.json();
      const suggestions = data.suggestions;

      if (suggestions && suggestions.length > 0) {
        const top = suggestions[0];
        const wiki = await fetchWikiDetails(top.name);

        setResult({
          name: top.name,
          commonName: top.commonName || wiki?.commonName || top.name.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          confidence: Math.round(top.probability * 100),
          description: wiki?.description || null,
          thumbnail: wiki?.thumbnail || null,
          wikiUrl: wiki?.wikiUrl || null,
          otherMatches: suggestions.slice(1, 3).map(s => ({
            name: s.name,
            confidence: Math.round(s.probability * 100),
          })),
        });

        const newCount = usageCount + 1;
        setUsageCount(newCount);
        await supabase.from('users').update({ free_uses: newCount }).eq('id', user.id);
      } else {
        setError('Could not identify the plant. Try a clearer photo.');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Something went wrong: ' + err.message);
    }

    setLoading(false);
  };

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const maxSize = 1024;
          const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        };
        img.onerror = reject;
        img.src = reader.result;
      };
      reader.onerror = reject;
    });

  const handleUpgrade = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, email: user.email }),
      });
      const data = await response.json();
      if (data.url) window.location.href = data.url;
    } catch (err) {
      setError('Could not start checkout. Please try again.');
    }
  };

  const remaining = FREE_LIMIT - usageCount;

  return (
    <div className="app">
      <div className="user-bar">
        <span>{user.email}</span>
        <button className="logout-btn" onClick={handleLogout}>Log out</button>
      </div>
      <header className="header">
        <h1>🌿 What Is This Plant?</h1>
        <p className="tagline">Identify any plant instantly</p>
      </header>

      <main className="main">
        <div className="upload-box">
          <label className="upload-label" htmlFor="file-input">
            {preview ? (
              <img src={preview} alt="Selected plant" className="preview-img" />
            ) : (
              <div className="upload-placeholder">
                <span className="upload-icon">🌿</span>
                <span>Take or upload a photo</span>
              </div>
            )}
          </label>
          <input
            id="file-input"
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="file-input"
          />
        </div>

        <div className="photo-buttons">
          <label className="photo-btn camera-btn" htmlFor="camera-input">
            📷 Take Photo
          </label>
          <input
            id="camera-input"
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleImageChange}
            className="file-input"
          />
          <label className="photo-btn gallery-btn" htmlFor="file-input">
            🖼️ Choose Photo
          </label>
        </div>

        <div className="usage-info">
          {isPro ? (
            <span>✅ Pro — unlimited identifications</span>
          ) : remaining > 0 ? (
            <span>{remaining} free identification{remaining !== 1 ? 's' : ''} remaining</span>
          ) : (
            <span className="usage-warning">No free identifications left</span>
          )}
        </div>

        <button
          className="identify-btn"
          onClick={handleIdentify}
          disabled={loading || !image || (!isPro && usageCount >= FREE_LIMIT)}
        >
          {loading ? 'Identifying...' : 'Identify Plant'}
        </button>

        {error && <div className="error">{error}</div>}

        {loading && (
          <div className="spinner-overlay">
            <div className="spinner"></div>
            <span className="spinner-text">Identifying your plant...</span>
          </div>
        )}

        {result && (
          <div className="result-card">
            {result.thumbnail && (
              <img src={result.thumbnail} alt={result.name} className="wiki-thumb" />
            )}
            <h2>{result.commonName}</h2>
            <p className="scientific-name"><em>{result.name}</em></p>
            <div className="confidence">
              Match confidence: <strong>{result.confidence}%</strong>
            </div>

            {result.description && (
              <p className="description">{result.description}</p>
            )}

            {result.wikiUrl && (
              <a href={result.wikiUrl} target="_blank" rel="noopener noreferrer" className="wiki-link">
                Read more on Wikipedia →
              </a>
            )}

            {result.otherMatches?.length > 0 && (
              <div className="other-matches">
                <p className="other-matches-title">Other possible matches:</p>
                {result.otherMatches.map((m, i) => (
                  <div key={i} className="other-match">
                    <span>{m.name}</span>
                    <span className="other-confidence">{m.confidence}%</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!isPro && usageCount >= FREE_LIMIT && (
          <div className="upgrade-box">
            <h3>Upgrade to Pro</h3>
            <p>Get unlimited plant identifications for just <strong>$4.99/month</strong></p>
            <button className="upgrade-btn" onClick={handleUpgrade}>Upgrade Now</button>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
